import { useForm } from '@tanstack/react-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { AuthLayout } from '@workspace/layout';
import { Button, CardContent, Label } from '@workspace/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  PasswordInput,
  errorToFormIssue,
  signInEmail,
} from '@workspace/ui';

import { auth } from '../firebase.tsx';

const emailSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

export default function SignIn() {
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      email: import.meta.env.DEV ? 'ezzabuzaid+devadmin@gmail.com' : '',
      password: import.meta.env.DEV ? 'zrdejZoXaFRSLOoMIp7e' : '',
    },
    validators: {
      onSubmit: emailSchema,
      onSubmitAsync: async (props) => {
        try {
          await signInEmail(auth, props.value.email, props.value.password);
          await navigate('/');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      className="bg-[#F8F8F8]"
      title="Sign In"
      subtitle="Enter your details below."
    >
      <CardContent className="px-0">
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field
            name="email"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email">
                  Email
                </Label>
                <EmailInput
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email-password">
                  Password
                </Label>
                <PasswordInput
                  id="email-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full cursor-pointer"
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
